/**
 * A month of days, and the date arithmetic the pickers share.
 *
 * RNR ships no calendar — its registry stops at Popover — so this is the one
 * written for the app. It is deliberately only the grid: the month header, the
 * weekday row, the days. The fields that open it add their own controls beneath,
 * because a date picker and a date-and-time picker want the same month and
 * different footers.
 *
 * Dates are `YYYY-MM-DD` strings taken apart by hand. Parsing a local date
 * string with `new Date` is where a timezone bug gets in, and these values are
 * local by contract.
 *
 * Weeks start on Sunday to match `weekdayLong0`, the index the availability
 * windows already use.
 */

import { Pressable, View } from 'react-native';
import { ChevronLeft, ChevronRight } from 'lucide-react-native';

import { Row, Stack } from './layout';
import { Icon } from './ui/icon';
import { Text } from './ui/text';
import { useLocale } from '../providers/locale';
import { cn } from '../lib/utils';

/** A month, zero-based as `Date` holds it. */
export interface CalendarMonth {
  year: number;
  /** Zero-based. */
  month: number;
}

/** The parts of a `YYYY-MM-DD` value. */
export interface CalendarDate extends CalendarMonth {
  day: number;
}

/** Two digits, as every date in the app is written. */
export const pad = (value: number) => String(value).padStart(2, '0');

const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Reads a `YYYY-MM-DD` value into its parts, without going through `Date`.
 *
 * @param value The form's value.
 * @returns The parts, or null when the value is empty or malformed.
 */
export function parseIsoDate(value: string): CalendarDate | null {
  const match = DATE_PATTERN.exec(value.trim());
  if (!match) return null;
  return { year: Number(match[1]), month: Number(match[2]) - 1, day: Number(match[3]) };
}

/**
 * Writes a date back as `YYYY-MM-DD`.
 *
 * @param year The full year.
 * @param month Zero-based.
 * @param day Day of the month.
 * @returns The value the form holds.
 */
export function toIsoDate(year: number, month: number, day: number): string {
  return `${year}-${pad(month + 1)}-${pad(day)}`;
}

/**
 * The month a value belongs to, for opening the grid on it.
 *
 * @param value A `YYYY-MM-DD` value.
 * @returns The month, or null when there is no usable value.
 */
export function monthOf(value: string): CalendarMonth | null {
  const parsed = parseIsoDate(value);
  return parsed ? { year: parsed.year, month: parsed.month } : null;
}

/**
 * Today, in the parts the calendar works in.
 *
 * @returns Today's date.
 */
export function todayParts(): CalendarDate {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth(), day: now.getDate() };
}

/** The last day of a month, which is day zero of the next. */
function lastDay(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

export interface CalendarProps {
  /** The selected date, as `YYYY-MM-DD`. An empty value selects nothing. */
  value: string;
  /**
   * The other end of a span, when the grid is marking a range rather than a day.
   *
   * The grid stays dumb about what a press means — the caller decides whether
   * this ends a span, starts one, or is a second date for a comparison. All the
   * grid does with it is fill the days between the two, so a range reads as one
   * thing rather than as two highlighted cells that happen to be apart.
   */
  endValue?: string;
  /**
   * The month on screen. The caller owns it so that its own Today action can
   * move the grid as well as the value.
   */
  month: CalendarMonth;
  onMonthChange: (month: CalendarMonth) => void;
  onChange: (value: string) => void;
}

/**
 * A month grid.
 *
 * @param props The selected value, the month on screen, and the handlers.
 * @returns The grid.
 */
export function Calendar({ value, month, onMonthChange, onChange, endValue }: CalendarProps) {
  const { t, locale } = useLocale();
  const selected = parseIsoDate(value);
  const today = todayParts();

  // A span, in calendar order: the caller may have handed the two ends over in
  // whichever order the merchant pressed them. ISO dates compare as strings.
  const span =
    endValue && parseIsoDate(endValue)
      ? { start: value < endValue ? value : endValue, end: value < endValue ? endValue : value }
      : null;

  const showMonth = (offset: number) => {
    const next = new Date(month.year, month.month + offset, 1);
    onMonthChange({ year: next.getFullYear(), month: next.getMonth() });
  };

  const leading = new Date(month.year, month.month, 1).getDay();
  const days = lastDay(month.year, month.month);
  const cells: Array<number | null> = [
    ...Array.from({ length: leading }, () => null),
    ...Array.from({ length: days }, (_, index) => index + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  const weekdayNames = Array.from({ length: 7 }, (_, index) =>
    new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(new Date(2024, 0, 7 + index))
  );

  const monthLabel = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(
    new Date(month.year, month.month, 1)
  );

  const isSelected = (day: number) =>
    selected?.year === month.year && selected?.month === month.month && selected?.day === day;
  const isToday = (day: number) =>
    today.year === month.year && today.month === month.month && today.day === day;

  return (
    <Stack gap="sm">
      <Row spread align="center">
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t('common.previousMonth')}
          onPress={() => showMonth(-1)}
          className="border-border h-8 w-8 items-center justify-center rounded-md border active:opacity-70">
          <Icon as={ChevronLeft} size={16} />
        </Pressable>
        <Text className="text-sm font-semibold">{monthLabel}</Text>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t('common.nextMonth')}
          onPress={() => showMonth(1)}
          className="border-border h-8 w-8 items-center justify-center rounded-md border active:opacity-70">
          <Icon as={ChevronRight} size={16} />
        </Pressable>
      </Row>

      {/* The same 7 × 36 as the grid below it, so every label stands over the
          column it names. This was a spread row, which is seven 36-point labels
          plus six of the kit's 12-point gaps — 324 points of content in a
          276-point column. It overflowed to the right and left each label 12
          points further right than its own column, 72 by Saturday, so the first
          of the month read as though it fell a day earlier than it does. */}
      <View className="w-[252px] flex-row">
        {weekdayNames.map((name, index) => (
          <View key={index} className="w-9 items-center">
            <Text className="text-subtle-foreground text-[11px]">{name}</Text>
          </View>
        ))}
      </View>

      {/* `w-[252px]` is 7 × the 36-point cell. Without it the grid takes the
          width it is given and wraps at eight days, which is what a popover
          sized by its contents did to it. */}
      <View className="w-[252px] flex-row flex-wrap">
        {cells.map((day, index) => {
          if (day === null) return <View key={index} className="h-9 w-9" />;
          const iso = toIsoDate(month.year, month.month, day);
          const isEnd = span !== null && (iso === span.start || iso === span.end);
          // Between the two ends, tinted rather than filled: the ends are the
          // answer, the days between are the extent of it. A day outside the
          // visible month belongs to no cell here, so a span crossing months
          // simply resumes on the next one.
          const inside = span !== null && iso > span.start && iso < span.end;
          const bare = span === null && isSelected(day);

          return (
            <Pressable
              key={index}
              accessibilityRole="button"
              accessibilityLabel={iso}
              // Toggle-button state rather than `aria-selected`: the cell's role
              // is `button`, which does not accept `selected`. The days inside a
              // span are not pressed — the two ends are — and the field above the
              // grid is what states the range in words.
              aria-pressed={bare || isEnd}
              onPress={() => onChange(iso)}
              className={cn(
                'h-9 w-9 items-center justify-center',
                isEnd
                  ? 'bg-primary rounded-md'
                  : inside
                    ? 'bg-primary/15 rounded-none'
                    : bare
                      ? 'bg-primary rounded-md'
                      : isToday(day)
                        ? 'border-primary rounded-md border'
                        : 'active:bg-accent rounded-md'
              )}>
              <Text
                className={cn(
                  'text-sm',
                  isEnd || bare ? 'text-primary-foreground font-semibold' : 'text-foreground'
                )}>
                {day}
              </Text>
            </Pressable>
          );
        })}
      </View>
    </Stack>
  );
}
